import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email and password required");
      return;
    }
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back!");
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      if (next) {
        navigate(next);
      } else if (u.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/21031387/pexels-photo-21031387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1128]/70 via-[#3B5BDB]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <p className="text-xs uppercase tracking-[0.3em] text-white/80">Mine AI</p>
          <h2 className="font-display text-4xl mt-3 leading-tight">
            Buy smart.<br />Save more.<br />Own with confidence.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <h1 className="font-display text-3xl sm:text-4xl text-[#0A1128]">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-[#4A5568]">
            Sign in to continue your jewellery journey.
          </p>
          <div className="mt-8 space-y-4">
            <Field label="Email" testid="login-email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email"
                className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Password" testid="login-password">
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password"
                className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="••••••••"
              />
            </Field>
            <div className="flex justify-end -mt-2">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs text-[#3B5BDB] hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
            <p className="text-sm text-center text-[#4A5568]">
              New to Mine AI?{" "}
              <Link to="/signup" className="text-[#3B5BDB] font-medium" data-testid="link-signup">
                Create an account
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#4A5568]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
